import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from '../../types';
import { formatPrice } from '../../utils/formatters';
import { Package, Plus, Trash2, Square, CheckSquare } from 'lucide-react';

interface Product {
    id: number;
    name: string;
    slug: string;
    price: number;
    category: string;
    media: string;
}

interface Props {
    currentUser: UserProfile;
    productsList: Product[];
    onDataNeededRefresh: () => void;
}

const ProductsTab: React.FC<Props> = ({ currentUser, productsList, onDataNeededRefresh }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<number | string | null>(null);

    const adminPath = location.pathname.split('/')[1];

    useEffect(() => {
        setSelectedIds(new Set());
    }, [productsList]);

    const handleSelectAll = () => {
        if (productsList.length > 0 && selectedIds.size === productsList.length) {
            setSelectedIds(new Set());
        } else {
            const allIds = new Set(productsList.map(item => item.id).filter(Boolean));
            setSelectedIds(allIds);
        }
    };

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = productsList.findIndex(p => p.id === id);
            const lastIndex = productsList.findIndex(p => p.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            for (let i = start; i <= end; i++) {
                if(productsList[i].id) newSelected.add(productsList[i].id!);
            }
        } else {
            if (newSelected.has(id)) newSelected.delete(id);
            else newSelected.add(id);
        }

        setSelectedIds(newSelected);
        setLastSelectedId(id);
    };

    const handleBulkDeleteProducts = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Delete ${selectedIds.size} products?`)) return;

        try {
            const res = await fetch('/api/shop/products', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                onDataNeededRefresh();
                setSelectedIds(new Set());
            }
        } catch (e) { console.error(e); }
    };

    const handleCreateProduct = () => {
        navigate(`/${adminPath}/products/new`);
    };

    const productColumns = [
        { key: 'id', label: 'ID', width: '60px' },
        { key: 'name', label: 'NAME' },
        { key: 'slug', label: 'SLUG' },
        { key: 'price', label: 'PRICE', width: '100px' },
        { key: 'category', label: 'CATEGORY (JSON)' },
    ];

    return (
        <div className="w-full relative animate-fade-in">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Package className="w-5 h-5" /> Products Manager
                    </h2>
                    <button 
                        onClick={handleCreateProduct}
                        className="flex items-center gap-1 bg-green-900/40 text-green-400 border border-green-800 hover:bg-green-900/60 px-3 py-1 rounded text-xs font-bold uppercase transition-colors"
                    >
                        <Plus className="w-4 h-4" /> Add Product
                    </button>
                    {selectedIds.size > 0 && (
                        <div className="flex items-center gap-2 bg-blue-900/30 border border-blue-700/50 rounded px-2 py-1">
                            <span className="text-xs text-blue-200">{selectedIds.size} selected</span>
                            <button onClick={handleBulkDeleteProducts} className="text-red-400 hover:text-red-300 p-1">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            <th className="p-2 border-r border-gray-800 w-10 text-center">
                                <div onClick={handleSelectAll} className="cursor-pointer flex justify-center text-gray-500 hover:text-white">
                                    {productsList.length > 0 && selectedIds.size === productsList.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                                </div>
                            </th>
                            {productColumns.map(col => (
                                <th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ width: col.width }}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {productsList.map(p => {
                            const isSelected = selectedIds.has(p.id);
                            return (
                                <tr key={p.id} className={`group hover:bg-mystic-btn ${isSelected ? 'bg-blue-900/10' : ''}`}>
                                    <td className="p-2 border-r border-gray-800 text-center">
                                        <div onClick={(e) => handleSelectRow(p.id, e)} className="cursor-pointer flex justify-center">
                                            {isSelected ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}
                                        </div>
                                    </td>
                                    {productColumns.map(col => (
                                        <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300">
                                            {col.key === 'name' ? (
                                                <button onClick={() => navigate(`/${adminPath}/products/edit/${p.id}`)} className="font-bold text-mystic-accent hover:underline text-left">
                                                    {p.name}
                                                </button>
                                            ) : col.key === 'price' ? (
                                                <span className="text-mystic-accent font-bold">{formatPrice(p.price)} zł</span>
                                            ) : col.key === 'category' || col.key === 'media' ? (
                                                <span className="text-gray-500 truncate block max-w-[200px]">{p[col.key as keyof Product]}</span>
                                            ) : (
                                                p[col.key as keyof Product]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProductsTab;